import { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import CampaignCard from './CampaignCard'; // uses shared component
import { Campaign } from '../types';
import { campaignService } from '../../../services/campaignService';

const CampaignsSection = () => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('All');

  const categories = ['All', 'Fashion', 'Technology', 'Health & Fitness', 'Food & Beverage', 'Travel', 'Gaming'];

  // Demo campaigns as fallback - using useMemo to prevent recreation
  // Premium demo campaigns as fallback
  const demoCampaigns = useMemo(() => [
    {
      id: 'demo-1',
      title: 'Obsidian Noir: Summer St. Edition',
      brand: 'StyleHub',
      brandName: 'StyleHub Luxe',
      brandLogo: 'https://images.unsplash.com/photo-1549298916-b41d501d3772?auto=format&fit=crop&q=80&w=100&h=100',
      brandLogoAlt: 'StyleHub logo',
      category: 'Fashion',
      budget: 85000,
      minViews: 20000,
      maxViews: 50000,
      payPerView: 1.25,
      deadline: '2026-06-15',
      description: 'The definitive high-fashion drop of the season. We are looking for creators who embody minimalist luxury.',
      requirements: ['Fashion-forward aesthetic', 'Instagram Reels focus', '50K+ Active Followers'],
      image: 'https://images.unsplash.com/photo-1539109136881-3be0616acf4b?auto=format&fit=crop&q=80&w=800&h=1000',
      imageAlt: 'Fashion model in obsidian streetwear',
      status: 'open' as const,
      applicants: 124,
      applicationsCount: 124,
      campaignMode: 'paid' as const
    },
    {
      id: 'demo-2',
      title: 'Nexus X1: The Future is Here',
      brand: 'AetherTech',
      brandName: 'Aether Dynamics',
      brandLogo: 'https://images.unsplash.com/photo-1614850523296-d8c1af93d400?auto=format&fit=crop&q=80&w=100&h=100',
      brandLogoAlt: 'AetherTech logo',
      category: 'Technology',
      budget: 125000,
      minViews: 100000,
      maxViews: 250000,
      payPerView: 0.85,
      deadline: '2026-05-20',
      description: 'Launch the ultra-thin Nexus X1. Focus on the holographic interface and battery performance.',
      requirements: ['Tech review expertise', 'YouTube/Tiktok', 'High engagement rates'],
      image: 'https://images.unsplash.com/photo-1511707171634-5f897ff02aa9?auto=format&fit=crop&q=80&w=800&h=1000',
      imageAlt: 'Futuristic smartphone with blue glow',
      status: 'open' as const,
      applicants: 89,
      applicationsCount: 89,
      campaignMode: 'paid' as const
    },
    {
      id: 'demo-3',
      title: 'Adrenaline Focus: Peak Performance',
      brand: 'IronForge',
      brandName: 'IronForge Athletics',
      brandLogo: 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?auto=format&fit=crop&q=80&w=100&h=100',
      brandLogoAlt: 'IronForge logo',
      category: 'Health & Fitness',
      budget: 45000,
      minViews: 15000,
      maxViews: 40000,
      payPerView: 0.65,
      deadline: '2026-07-01',
      description: 'Join the IronForge elite. We need high-intensity content that showcases grit and determination.',
      requirements: ['Fitness/Gym content', 'Dynamic videography', 'Weekly posting'],
      image: 'https://images.unsplash.com/photo-1517836357463-d25dfeac3438?auto=format&fit=crop&q=80&w=800&h=1000',
      imageAlt: 'Athlete in a high-intensity workout',
      status: 'open' as const,
      applicants: 256,
      applicationsCount: 256,
      campaignMode: 'barter' as const,
      productValue: 12000
    }
  ], []);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const fetchCampaigns = async () => {
      setIsLoading(true);
      try {
        const allCampaigns = await campaignService.getAllCampaigns();
        
        if (allCampaigns && allCampaigns.length > 0) {
          const mapped: Campaign[] = allCampaigns.slice(0, 6).map((c) => ({
            id: c.id,
            title: c.title,
            brand: 'Brand',
            brandLogo: c.image || '',
            brandLogoAlt: `${c.title} campaign`,
            category: c.category,
            budget: c.budget,
            viewsRequired: c.targetReach,
            pricePerView: c.costPerView,
            deadline: c.endDate.toISOString().split('T')[0],
            description: c.description,
            requirements: [],
            image: c.image || '',
            imageAlt: c.alt || `${c.title} image`,
            status: c.status as 'active' | 'pending' | 'completed',
            applicants: c.applicationsCount,
          }));
          console.log('Loaded real campaigns:', mapped.length);
          setCampaigns(mapped);
        } else {
          // Use demo campaigns if no real campaigns are available
          console.log('No real campaigns, using demo campaigns:', demoCampaigns.length);
          setCampaigns(demoCampaigns);
        }
      } catch (error) {
        console.error('Error fetching campaigns:', error);
        // Use demo campaigns on error
        console.log('Error loading campaigns, using demo campaigns:', demoCampaigns.length);
        setCampaigns(demoCampaigns);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCampaigns();
  }, [demoCampaigns]);

  const filteredCampaigns = selectedCategory === 'All' 
    ? campaigns 
    : campaigns.filter(c => c.category === selectedCategory);

  const visibleCampaigns = useMemo(() => {
    if (isMobile) return [filteredCampaigns[currentIndex]];
    return filteredCampaigns.slice(currentIndex, currentIndex + 3);
  }, [filteredCampaigns, currentIndex, isMobile]);

  return (
    <section className="py-24 sm:py-32 lg:py-40 bg-[#0a0a0c] relative overflow-hidden">
      {/* Background Mesh Gradients */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary/10 rounded-full blur-[120px] animate-pulse pointer-events-none"></div>
      <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-red-600/5 rounded-full blur-[140px] animate-pulse pointer-events-none" style={{ animationDelay: '2s' }}></div>
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-[url('https://www.transparenttextures.com/patterns/dark-matter.png')] opacity-20 pointer-events-none"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-20">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-6 py-2 bg-white/[0.03] border border-white/[0.08] backdrop-blur-md rounded-full mb-8"
          >
            <div className="w-2 h-2 rounded-full bg-primary animate-ping" />
            <span className="text-sm font-bold text-white/70 uppercase tracking-[0.2em]">Live Opportunities</span>
          </motion.div>
          
          <h2 className="text-6xl sm:text-7xl lg:text-8xl font-black text-white mb-8 tracking-tighter leading-[0.9]">
            FEATURED <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-red-500 to-primary bg-[length:200%_auto] animate-gradient-x">OPPORTUNITIES</span>
          </h2>
          
          <p className="text-xl sm:text-2xl text-white/40 max-w-3xl mx-auto leading-relaxed font-medium">
            Join elite campaigns from forward-thinking brands. <br className="hidden md:block" />
            Start earning based on <span className="text-white/80">real impact</span>.
          </p>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-3 mb-16">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => {
                setSelectedCategory(category);
                setCurrentIndex(0);
              }}
              className={`px-8 py-3.5 rounded-2xl font-bold transition-all duration-500 backdrop-blur-md border ${
                selectedCategory === category
                  ? 'bg-primary text-white border-primary shadow-[0_0_30px_rgba(255,59,59,0.3)] scale-105'
                  : 'bg-white/[0.03] text-white/40 border-white/[0.05] hover:border-white/20 hover:text-white/70'
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white rounded-3xl border-2 border-gray-100 overflow-hidden animate-pulse shadow-xl">
                <div className="h-56 bg-gradient-to-br from-gray-200 to-gray-300" />
                <div className="p-8 space-y-4">
                  <div className="h-6 bg-gray-200 rounded w-3/4" />
                  <div className="h-4 bg-gray-200 rounded w-1/2" />
                  <div className="h-24 bg-gray-200 rounded" />
                  <div className="h-12 bg-gray-200 rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : filteredCampaigns.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-primary to-red-600 rounded-full flex items-center justify-center">
              <Icon name="Search" size={48} className="text-white" />
            </div>
            <h3 className="text-3xl font-bold text-black mb-4">No Campaigns Available</h3>
            <p className="text-gray-600 text-lg max-w-md mx-auto">
              Check back soon for exciting new campaign opportunities!
            </p>
          </div>
        ) : (
          <div className="relative">
            <div className={`grid ${isMobile ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'} gap-8 lg:gap-12`}>
              {visibleCampaigns.map((campaign, index) => (
                <motion.div
                  key={campaign.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="group relative"
                >
                  <div className="absolute -inset-2 bg-gradient-to-b from-primary/20 to-transparent rounded-[2.5rem] blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="relative p-px rounded-[2rem] bg-gradient-to-b from-white/10 to-transparent group-hover:from-primary/40 transition-all duration-500">
                    <div className="rounded-[2rem] bg-[#121214] overflow-hidden">
                      <CampaignCard campaign={campaign} />
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {filteredCampaigns.length > (isMobile ? 1 : 3) && (
              <div className="flex items-center justify-center gap-6 mt-12">
                <button
                  onClick={() => setCurrentIndex(prev => prev === 0 ? filteredCampaigns.length - 1 : prev - 1)}
                  className="p-5 rounded-2xl bg-white/[0.03] border border-white/[0.08] backdrop-blur-md text-white hover:bg-white/[0.08] hover:border-primary/50 hover:text-primary transition-all duration-300"
                  aria-label="Previous campaigns"
                >
                  <Icon name="ChevronLeft" size={24} />
                </button>
                
                <div className="flex items-center gap-2.5">
                  {filteredCampaigns.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentIndex(index)}
                      className={`rounded-full transition-all duration-300 ${
                        index === currentIndex 
                          ? 'bg-primary w-8 h-1.5 shadow-[0_0_15px_rgba(255,59,59,0.5)]' : 'bg-white/10 w-1.5 h-1.5 hover:bg-white/30'
                      }`}
                      aria-label={`Go to slide ${index + 1}`}
                    />
                  ))}
                </div>

                <button
                  onClick={() => setCurrentIndex(prev => prev === filteredCampaigns.length - 1 ? 0 : prev + 1)}
                  className="p-5 rounded-2xl bg-white/[0.03] border border-white/[0.08] backdrop-blur-md text-white hover:bg-white/[0.08] hover:border-primary/50 hover:text-primary transition-all duration-300"
                  aria-label="Next campaigns"
                >
                  <Icon name="ChevronRight" size={24} />
                </button>
              </div>
            )}
          </div>
        )}

        <div className="text-center mt-24">
          <button className="group relative px-12 py-6 bg-white text-black rounded-full font-black text-xl overflow-hidden transition-all duration-300 hover:scale-105 hover:shadow-[0_0_40px_rgba(255,255,255,0.2)]">
            <span className="relative z-10 flex items-center gap-3">
              Explore All Campaigns
              <Icon name="ArrowRight" size={24} className="group-hover:translate-x-2 transition-transform" />
            </span>
          </button>
        </div>
      </div>
    </section>
  );
};

export default CampaignsSection;
